import type { CSSProperties, JSX, KeyboardEvent, ReactNode } from "react";
import { useId, useRef, useState } from "react";
import { cx } from "../cx";

export interface TabItem {
  value: string;
  label: ReactNode;
  /** Optional content rendered in the tabpanel while this tab is active. */
  panel?: ReactNode | undefined;
}

export interface TabsProps {
  /** Tabs as bare strings or `{ value, label, panel? }` items. */
  tabs?: ReadonlyArray<string | TabItem> | undefined;
  /** Controlled active value. */
  value?: string | undefined;
  /** Uncontrolled initial value (defaults to the first tab). */
  defaultValue?: string | undefined;
  onChange?: ((value: string) => void) | undefined;
  className?: string | undefined;
  style?: CSSProperties | undefined;
}

/**
 * Underline tabs — the volt underline marks the active tab. Arrow keys, Home,
 * and End move and select within the tablist (roving tabindex). Ported from
 * design/tarski-kit/components/display/Tabs.jsx.
 */
export function Tabs({
  tabs = [],
  value,
  defaultValue,
  onChange,
  className,
  style,
}: TabsProps): JSX.Element {
  const items: TabItem[] = tabs.map((tab) =>
    typeof tab === "string" ? { value: tab, label: tab } : tab,
  );
  const [internal, setInternal] = useState<string | undefined>(
    () => defaultValue ?? items[0]?.value,
  );
  const current = value !== undefined ? value : internal;
  const baseId = useId();
  const tabRefs = useRef<Array<HTMLButtonElement | null>>([]);

  const pick = (next: string) => {
    if (value === undefined) {
      setInternal(next);
    }
    onChange?.(next);
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLButtonElement>, index: number) => {
    const count = items.length;
    if (count === 0) {
      return;
    }
    let next: number;
    if (event.key === "ArrowRight") {
      next = (index + 1) % count;
    } else if (event.key === "ArrowLeft") {
      next = (index - 1 + count) % count;
    } else if (event.key === "Home") {
      next = 0;
    } else if (event.key === "End") {
      next = count - 1;
    } else {
      return;
    }
    event.preventDefault();
    const item = items[next];
    if (item === undefined) {
      return;
    }
    pick(item.value);
    tabRefs.current[next]?.focus();
  };

  const activeIndex = items.findIndex((item) => item.value === current);
  const focusIndex = activeIndex >= 0 ? activeIndex : 0;
  const activeItem = activeIndex >= 0 ? items[activeIndex] : undefined;
  const hasPanels = items.some((item) => item.panel !== undefined);
  const tabId = (index: number) => `${baseId}-tab-${index}`;
  const panelId = (index: number) => `${baseId}-panel-${index}`;

  return (
    <div className={cx("tk-tabs", className)} style={style}>
      <div role="tablist" className="tk-tabs__list">
        {items.map((item, index) => {
          const selected = item.value === current;
          return (
            <button
              key={item.value}
              ref={(element) => {
                tabRefs.current[index] = element;
              }}
              type="button"
              role="tab"
              id={tabId(index)}
              aria-selected={selected}
              aria-controls={hasPanels ? panelId(index) : undefined}
              tabIndex={index === focusIndex ? 0 : -1}
              className="tk-tabs__tab"
              onClick={() => pick(item.value)}
              onKeyDown={(event) => handleKeyDown(event, index)}
            >
              {item.label}
            </button>
          );
        })}
      </div>
      {hasPanels && activeIndex >= 0 ? (
        <div
          role="tabpanel"
          id={panelId(activeIndex)}
          aria-labelledby={tabId(activeIndex)}
          className="tk-tabs__panel"
        >
          {activeItem?.panel}
        </div>
      ) : null}
    </div>
  );
}
